import { formatISO, parseISO } from 'date-fns'
import { Draft } from 'immer'
import { Get } from 'type-fest'
import { computeDurationFromDates } from '@opallabs/lambda'
import { noCategoryKey } from '..'
import { PlanResponse } from '../types'
import { changeBlockCategory } from './changeBlockCategory'

type BlockDef = NonNullable<
  Get<PlanResponse, ['data', 'blocks', 'by_id', string]>
>

export type BlockAttributes = {
  categoryId?: BlockDef['category_id']
  categoryType?: BlockDef['category_type']
  color?: BlockDef['color']
  description?: BlockDef['description']
  duration?: BlockDef['duration']
  endDate?: Date | null
  imageId?: BlockDef['image_id']
  row?: BlockDef['row_index']
  startDate?: Date | null
  title?: BlockDef['title']
  yScale?: BlockDef['y_scale']
  connector?: Omit<BlockDef['connectors'][number], 'view_id'>
}

export const updateBlock = (blockId: string, updateAttrs: BlockAttributes) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const block = draftResponse.data.blocks.by_id[blockId]

    if (!block) {
      return draftResponse
    }

    const previousCategoryId = block.category_id
    const connector =
      updateAttrs.connector &&
      block.connectors.find((c) => c.id === updateAttrs.connector?.id)
    const previousConnectorCategoryId = connector?.category_id

    if (updateAttrs.categoryId !== undefined) {
      block.category_id = updateAttrs.categoryId
    }

    if (connector && updateAttrs.connector) {
      connector.category_id = block.category_id =
        updateAttrs.connector.category_id
    }

    if (updateAttrs.categoryType !== undefined) {
      block.category_type = updateAttrs.categoryType
    }

    if (updateAttrs.color !== undefined) {
      block.color = updateAttrs.color
    }

    if (updateAttrs.description !== undefined) {
      block.description = updateAttrs.description
    }

    if (updateAttrs.startDate !== undefined) {
      block.start_at = updateAttrs.startDate
        ? formatISO(updateAttrs.startDate, { representation: 'date' })
        : null
    }

    if (updateAttrs.duration !== undefined) {
      block.duration = updateAttrs.duration
    }

    if (updateAttrs.endDate !== undefined) {
      if (updateAttrs.endDate === null) {
        block.duration = null
      } else {
        const startDate =
          updateAttrs.startDate ??
          (block.start_at ? parseISO(block.start_at) : null)
        block.duration = startDate
          ? computeDurationFromDates(startDate, updateAttrs.endDate)
          : null
      }
    }

    if (updateAttrs.row !== undefined) {
      block.row_index = updateAttrs.row
    }

    if (updateAttrs.title !== undefined) {
      block.title = updateAttrs.title
    }

    if (updateAttrs.yScale !== undefined) {
      block.y_scale = updateAttrs.yScale
    }

    // Move the block after all fields change, so its new bucket position and
    // the overlap check use the final row and dates.
    const categoryMove =
      updateAttrs.categoryId !== undefined
        ? { from: previousCategoryId, to: updateAttrs.categoryId }
        : connector && updateAttrs.connector
          ? {
              from: previousConnectorCategoryId,
              to: updateAttrs.connector.category_id,
            }
          : undefined

    if (categoryMove) {
      // The BFF lists a block that has a swimlane but no start date in both
      // the pre-planning bucket and its swimlane bucket.
      const staysInPreplanning =
        categoryMove.from === null &&
        categoryMove.to !== null &&
        block.start_at === null

      changeBlockCategory(blockId, {
        categoryIdToInsertInto: categoryMove.to,
        categoryIdToRemoveFrom: staysInPreplanning
          ? noCategoryKey
          : categoryMove.from,
      })(draftResponse)
    }

    // A position that was updated in place keeps the row of its bucket, so
    // give it the new row here.
    if (updateAttrs.row !== undefined && block.category_id) {
      const position = draftResponse.data.blocks.by_category_with_position[
        block.category_id
      ]?.find(({ id }) => id === blockId)

      if (position) position.row_index = block.row_index
    }

    return draftResponse
  }
}
